// Note: This is expected due to the use of the render function.
/* eslint-disable vue/one-component-per-file */

import {
  defineComponent,
  h,
  inject,
  nextTick,
  provide,
  ref,
  toRefs,
  watch,
  onMounted,
} from 'vue';

/**
 * Creates a context with provider and consumer functions
 * @param {string} id - The context identifier
 * @returns {Array} - Array containing [provider, consumer, contextId]
 */
export function createContext(id) {
  const contextId = Symbol(id);
  const provider = value => provide(contextId, value);
  const consumer = fallback => inject(contextId, fallback);

  return [provider, consumer, contextId];
}

export const [TransitionProvider, useTransitionContext] =
  createContext('TransitionContext');

export function useTransition(props, emit) {
  const isRootMounted = ref(false);
  const isChildMounted = ref(false);

  const mountRoot = () => {
    isRootMounted.value = true;

    nextTick(() => {
      isChildMounted.value = true;

      emit('open');
    });
  };

  const unmountRoot = () => {
    nextTick(() => {
      isRootMounted.value = false;
      emit('close');
    });
  };

  watch(
    () => props.isOpen,
    value => {
      if (value) {
        mountRoot();
      } else {
        isChildMounted.value = false;
      }
    }
  );

  onMounted(() => {
    if (props.isOpen) {
      mountRoot();
    }
  });

  return {
    isRootMounted,
    isChildMounted,
    mountRoot,
    unmountRoot,
  };
}

export const CsTransitionRoot = defineComponent({
  name: 'CsTransitionRoot',
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['open', 'close'],
  setup(props, { slots, emit, attrs }) {
    const { isRootMounted, isChildMounted, unmountRoot, mountRoot } =
      useTransition(props, emit);

    TransitionProvider({
      isRootMounted,
      isChildMounted,
      mountRoot,
      unmountRoot,
      rootProps: toRefs(props),
    });

    return () => {
      const children = slots.default && slots.default({ isOpen: props.isOpen });
      const contentNode = h(
        'div',
        {
          class: attrs.class,
          attrs: {
            'data-component': 'CsTransitionRoot',
          },
        },
        children
      );

      return isRootMounted.value ? contentNode : null;
    };
  },
});

export const CsTransitionChild = defineComponent({
  name: 'CsTransitionChild',
  props: {
    enterActiveClass: {
      type: String,
      default: '',
    },
    enterClass: {
      type: String,
      default: '',
    },
    enterToClass: {
      type: String,
      default: '',
    },
    leaveActiveClass: {
      type: String,
      default: '',
    },
    leaveClass: {
      type: String,
      default: '',
    },
    leaveToClass: {
      type: String,
      default: '',
    },
  },
  setup(props, { slots }) {
    return () => {
      const { isChildMounted, unmountRoot } = useTransitionContext();

      const children =
        slots.default && slots.default({ isOpen: isChildMounted.value });

      return h(
        'Transition',
        {
          attrs: {
            enterActiveClass: props.enterActiveClass,
            enterClass: props.enterClass,
            enterToClass: props.enterToClass,
            leaveActiveClass: props.leaveActiveClass,
            leaveClass: props.leaveClass,
            leaveToClass: props.leaveToClass,
          },
          on: {
            afterLeave: () => {
              unmountRoot();
            },
          },
        },
        isChildMounted.value ? children : null
      );
    };
  },
});
